import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { theme } from "../../../Theme/index";
import FacebookCircularProgress from "../../shared/shared/Loading";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import ColoredBox from "../../Boxes/TopProductBox/ColoredBox";



const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  return Math.round(num).toLocaleString("en-US");
};



const PercentageCell = ({ value, brandName, maxPositive, maxNegative }) => {
  if (value === "-" || value === null || value === undefined || value === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#666",
        }}
      >
        -
      </Box>
    );
  }

  const positive = value >= 0;
  const absValue = Math.abs(value);
  
  const maxValue = positive ? maxPositive : maxNegative;
  
  const relativeWidth = maxValue > 0 ? (absValue / maxValue) * 40 : 0;

  const finalWidth = value === 0 ? 0 : relativeWidth;

  const tooltipText = `${brandName}: ${value}% ${
    value > 0 ? "(Increase)" : value < 0 ? "(Decrease)" : ""
  }`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: 32,
        position: "relative",
      }}
    >
      <Tooltip
        title={tooltipText}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "rgba(26, 26, 26, 0.8)",
              fontSize: "12px",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            height: "100%",
            width: `${finalWidth}%`,
            backgroundColor: positive ? theme.palette.primary.main : "#d32f2f",
            borderRadius: 1,
            left: positive ? "50%" : `${50 - finalWidth}%`,
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        />
      </Tooltip>

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: "#999",
        }}
      />

      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          left: positive ? `${50 + finalWidth + 2}%` : "auto",
          right: positive ? "auto" : `${50 + finalWidth + 2}%`,
          color: positive ? theme.palette.primary.main : "#d32f2f",
          fontSize: "11px",
          fontWeight: "bold",
          whiteSpace: "nowrap",
        }}
      >
        {typeof value === 'number' ? value.toFixed(1) : value}%
      </Typography>
    </Box>
  );
};

PercentageCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  brandName: PropTypes.string.isRequired,
  maxPositive: PropTypes.number.isRequired,
  maxNegative: PropTypes.number.isRequired,
};

export default function PromotionsTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedPeriod, setSelectedPeriod] = useState("P12M");
  const [availablePeriods, setAvailablePeriods] = useState([]);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [nestedData, setNestedData] = useState({});

  
  // Responsive breakpoints
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const fetchPromotionData = async () => {
  //   if (!projectId) {
  //     setError("Project ID not found.");
  //     setLoading(false);
  //     return;
  //   }

  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const apiUrl = `${getUrl()}/strategy_creation/promo_evolution?project_id=${projectId}&hierarchy=${selectedHierarchy}&time_period=${timePeriod}&filters={}&target=${target}`;
      
  //     const response = await fetch(apiUrl, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //         "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! Status: ${response.status}`);
  //     }

  //     const responseText = await response.text();
  //     console.log(responseText);

  //     let result;
  //     try {
  //       const cleanedResponse = responseText.replace(/NaN/g, "null");
  //       result = JSON.parse(cleanedResponse);
  //     } catch (parseError) {
  //       console.error("JSON Parse Error:", parseError);
  //       throw new Error("Failed to parse API response");
  //     }


  //     if (!result || typeof result !== 'object') {
  //       throw new Error("Invalid API response format");
  //     }

  //   const transformedData = transformApiData(result);
  //   setData(transformedData);

  //   if (transformedData.length > 0) {
  //     const allValues = transformedData.flatMap((row) => [
  //       row.vsodIya,
  //       row.uplift,
  //     ]);
  //     const positives = allValues.filter((v) => v > 0);
  //     const negatives = allValues
  //       .filter((v) => v < 0)
  //       .map((v) => Math.abs(v));

  //     setMaxPositive(positives.length ? Math.max(...positives) : 100);
  //     setMaxNegative(negatives.length ? Math.max(...negatives) : 100);
  //   }
  //   } catch (err) {
  //     console.error("Error fetching promotion data:", err);
  //     setError(err.message);
  //   } finally {
  //   setLoading(false);
  //   }
  // };


  const fetchPromotionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const raw = [
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "AFIA",
    "year": 2023,
    "value_sales": 59410323.74,
    "volume_sales": 5233612.0,
    "volume_sales_non_promo": 1064682.9,
    "VSOD": 4168929.1,
    "volume_sales_CP": 1734308.1,
    "volume_sales_PX": 1255442.8,
    "count_CP": 18671.0,
    "count_PX": 5024.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 669625.2,
    "baseline_volume": 1064682.9,
    "baseline_PX": 1064682.9,
    "baseline_CP": 1064682.9,
    "uplift_pct": 2.915653289819908,
    "productivity_PX": 249.889092,
    "productivity_CP": 35.864453
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "AFIA",
    "year": 2024,
    "value_sales": 52189661.61,
    "volume_sales": 5378209.1,
    "volume_sales_non_promo": 842028.2,
    "VSOD": 4536180.9,
    "volume_sales_CP": 1148765.6,
    "volume_sales_PX": 2480251.9,
    "count_CP": 25065.0,
    "count_PX": 8675.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 306737.4,
    "baseline_volume": 842028.2,
    "baseline_PX": 842028.2,
    "baseline_CP": 842028.2,
    "uplift_pct": 4.387207815605225,
    "productivity_PX": 285.908,
    "productivity_CP": 12.237678
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "AL ARABI",
    "year": 2023,
    "value_sales": 32844586.92,
    "volume_sales": 3121072.8,
    "volume_sales_non_promo": 439938.6,
    "VSOD": 2681134.2,
    "volume_sales_CP": 655952.3,
    "volume_sales_PX": 1422661.8,
    "count_CP": 7568.0,
    "count_PX": 3263.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 216013.7,
    "baseline_volume": 439938.6,
    "baseline_PX": 439938.6,
    "baseline_CP": 439938.6,
    "uplift_pct": 5.0943372552442545,
    "productivity_PX": 435.9981,
    "productivity_CP": 28.543036
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "AL ARABI",
    "year": 2024,
    "value_sales": 35936610.62,
    "volume_sales": 4749607.8,
    "volume_sales_non_promo": 1168378.8,
    "VSOD": 3581229.0,
    "volume_sales_CP": 674166.6,
    "volume_sales_PX": 1603122.7,
    "count_CP": 7743.0,
    "count_PX": 5233.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -494212.2,
    "baseline_volume": 1168378.8,
    "baseline_PX": 1168378.8,
    "baseline_CP": 1168378.8,
    "uplift_pct": 2.065126652417863,
    "productivity_PX": 306.348691,
    "productivity_CP": -63.826966
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "DALAL",
    "year": 2023,
    "value_sales": 2086303.11,
    "volume_sales": 318928.5,
    "volume_sales_non_promo": 100485.0,
    "VSOD": 218443.5,
    "volume_sales_CP": 1092.0,
    "volume_sales_PX": 116866.5,
    "count_CP": 35.0,
    "count_PX": 547.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -99393.0,
    "baseline_volume": 100485.0,
    "baseline_PX": 100485.0,
    "baseline_CP": 100485.0,
    "uplift_pct": 1.1738916256157637,
    "productivity_PX": 213.649909,
    "productivity_CP": -2839.8
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "DALAL",
    "year": 2024,
    "value_sales": 1960486.58,
    "volume_sales": 309190.5,
    "volume_sales_non_promo": 17731.5,
    "VSOD": 291459.0,
    "volume_sales_CP": 80602.5,
    "volume_sales_PX": 185295.0,
    "count_CP": 2657.0,
    "count_PX": 1428.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 62871.0,
    "baseline_volume": 17731.5,
    "baseline_PX": 17731.5,
    "baseline_CP": 17731.5,
    "uplift_pct": 15.4373572455799,
    "productivity_PX": 129.758403,
    "productivity_CP": 23.662401
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "HALAH",
    "year": 2023,
    "value_sales": 23126820.79,
    "volume_sales": 3184838.3,
    "volume_sales_non_promo": 623755.9,
    "VSOD": 2561082.4,
    "volume_sales_CP": 694012.0,
    "volume_sales_PX": 1205340.5,
    "count_CP": 5655.0,
    "count_PX": 2016.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 70256.1,
    "baseline_volume": 623755.9,
    "baseline_PX": 623755.9,
    "baseline_CP": 623755.9,
    "uplift_pct": 3.105904890037914,
    "productivity_PX": 597.887153,
    "productivity_CP": 12.423714
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "HALAH",
    "year": 2024,
    "value_sales": 17370465.72,
    "volume_sales": 2831320.6,
    "volume_sales_non_promo": 205977.5,
    "VSOD": 2625343.1,
    "volume_sales_CP": 638499.4,
    "volume_sales_PX": 1763032.7,
    "count_CP": 7584.0,
    "count_PX": 2759.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 432521.9,
    "baseline_volume": 205977.5,
    "baseline_PX": 205977.5,
    "baseline_CP": 205977.5,
    "uplift_pct": 11.745776116323384,
    "productivity_PX": 639.01149,
    "productivity_CP": 57.030841
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "LEEZA",
    "year": 2023,
    "value_sales": 2041486.44,
    "volume_sales": 193311.9,
    "volume_sales_non_promo": 25092.8,
    "VSOD": 168219.1,
    "volume_sales_CP": 168219.1,
    "volume_sales_PX": -25092.8,
    "count_CP": 7203.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 143126.3,
    "baseline_volume": 25092.8,
    "baseline_PX": 25092.8,
    "baseline_CP": 25092.8,
    "uplift_pct": 5.703879200408085,
    "productivity_PX": null,
    "productivity_CP": 19.870373
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "LEEZA",
    "year": 2024,
    "value_sales": 1155894.16,
    "volume_sales": 108850.1,
    "volume_sales_non_promo": 17145.9,
    "VSOD": 91704.2,
    "volume_sales_CP": 81511.9,
    "volume_sales_PX": -17145.9,
    "count_CP": 7620.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 64366.0,
    "baseline_volume": 17145.9,
    "baseline_PX": 17145.9,
    "baseline_CP": 17145.9,
    "uplift_pct": 4.3484623146058246,
    "productivity_PX": null,
    "productivity_CP": 8.446982
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "NAKHEEL",
    "year": 2023,
    "value_sales": 15439224.02,
    "volume_sales": 2062003.5,
    "volume_sales_non_promo": 488095.3,
    "VSOD": 1573908.2,
    "volume_sales_CP": 465476.7,
    "volume_sales_PX": 619701.7,
    "count_CP": 5260.0,
    "count_PX": 2226.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -22618.6,
    "baseline_volume": 488095.3,
    "baseline_PX": 488095.3,
    "baseline_CP": 488095.3,
    "uplift_pct": 2.2245920007834536,
    "productivity_PX": 278.392498,
    "productivity_CP": -4.300114
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "NAKHEEL",
    "year": 2024,
    "value_sales": 10860319.79,
    "volume_sales": 1782663.2,
    "volume_sales_non_promo": 131256.3,
    "VSOD": 1651406.9,
    "volume_sales_CP": 465351.6,
    "volume_sales_PX": 1046823.5,
    "count_CP": 8115.0,
    "count_PX": 2735.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 334095.3,
    "baseline_volume": 131256.3,
    "baseline_PX": 131256.3,
    "baseline_CP": 131256.3,
    "uplift_pct": 11.58154389541683,
    "productivity_PX": 382.750823,
    "productivity_CP": 41.170092
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "O LITE",
    "year": 2023,
    "value_sales": 3435735.01,
    "volume_sales": 247674.0,
    "volume_sales_non_promo": 86574.0,
    "VSOD": 161100.0,
    "volume_sales_CP": 150429.0,
    "volume_sales_PX": -86574.0,
    "count_CP": 3818.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 63855.0,
    "baseline_volume": 86574.0,
    "baseline_PX": 86574.0,
    "baseline_CP": 86574.0,
    "uplift_pct": 0.8608358167579181,
    "productivity_PX": null,
    "productivity_CP": 16.724725
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "O LITE",
    "year": 2024,
    "value_sales": 3161257.02,
    "volume_sales": 319563.0,
    "volume_sales_non_promo": 63276.0,
    "VSOD": 256287.0,
    "volume_sales_CP": 156312.0,
    "volume_sales_PX": 6609.0,
    "count_CP": 3855.0,
    "count_PX": 940.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 93036.0,
    "baseline_volume": 63276.0,
    "baseline_PX": 63276.0,
    "baseline_CP": 63276.0,
    "uplift_pct": 3.050303432581073,
    "productivity_PX": 7.030851,
    "productivity_CP": 24.133852
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "SHAMS",
    "year": 2023,
    "value_sales": 34112692.43,
    "volume_sales": 3287227.4,
    "volume_sales_non_promo": 365098.3,
    "VSOD": 2922129.1,
    "volume_sales_CP": 619935.9,
    "volume_sales_PX": 1801851.9,
    "count_CP": 7555.0,
    "count_PX": 4150.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 254837.6,
    "baseline_volume": 365098.3,
    "baseline_PX": 365098.3,
    "baseline_CP": 365098.3,
    "uplift_pct": 7.0036776396932,
    "productivity_PX": 434.181181,
    "productivity_CP": 33.730986
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "SHAMS",
    "year": 2024,
    "value_sales": 33147795.25,
    "volume_sales": 4566406.1,
    "volume_sales_non_promo": 957595.6,
    "VSOD": 3608810.5,
    "volume_sales_CP": 682318.4,
    "volume_sales_PX": 1940455.3,
    "count_CP": 10509.0,
    "count_PX": 5214.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -275277.2,
    "baseline_volume": 957595.6,
    "baseline_PX": 957595.6,
    "baseline_CP": 957595.6,
    "uplift_pct": 2.7686164180370083,
    "productivity_PX": 372.162505,
    "productivity_CP": -26.194424
  },
  {
    "manufacturer": "SAVOLA GROUP",
    "brand": "SLITE",
    "year": 2023,
    "value_sales": 150515.36,
    "volume_sales": 16824.0,
    "volume_sales_non_promo": 4621.5,
    "VSOD": 12202.5,
    "volume_sales_CP": 0.0,
    "volume_sales_PX": -4621.5,
    "count_CP": 0.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -4621.5,
    "baseline_volume": 4621.5,
    "baseline_PX": 4621.5,
    "baseline_CP": 4621.5,
    "uplift_pct": 1.6403765011359948,
    "productivity_PX": null,
    "productivity_CP": null
  }
];

      const staticData = raw.map(it => ({
      ...it,
      Time_Granularity: it.year === 2024 ? "P12M" : it.year === 2023 ? "P24M" : String(it.year),
    }));

      //const years = [...new Set(staticData.map(item => item.year))].sort((a, b) => b - a);
      //setAvailableYears(years);

      //const filteredData = staticData.filter(item => item.year === parseInt(selectedYear));
      const periods = [...new Set(staticData.map(i => i.Time_Granularity))].sort((a, b) =>
      a === "P12M" ? -1 : a.localeCompare(b)
    );

   setAvailablePeriods(periods);

   const filteredData = staticData.filter(i => i.Time_Granularity === selectedPeriod);

      const transformedData = transformApiData(filteredData);
      setData(transformedData);
    } catch (err) {
      console.error("Error loading promotion data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const transformApiData = (apiData) => {
    if (!apiData) return [];

  const safeParseNumber = (value) => {
    if (value === null || value === undefined || value === "NaN" || isNaN(value)) return 0;
    return Math.round(parseFloat(value) * 100) / 100;
  };

  if (Array.isArray(apiData)) {
    return apiData.map((item) => ({
      manufacturer: item.manufacturer || null,
      brand: item.brand || item.class || item.manufacturer || "Unknown",
      class: item.class || null,

      volumeSales: Math.round(item.volume_sales || 0),
      volumeSalesNonPromo: Math.round(item.volume_sales_non_promo || 0),
      vsod: Math.round(item.VSOD || 0),

      volumeSalesCP: Math.round(item.volume_sales_CP || 0),
      volumeSalesPX: Math.round(item.volume_sales_PX || 0),

      baselineVolume: Math.round(item.baseline_volume || 0),
      baselineCP: Math.round(item.baseline_CP || 0),
      baselinePX: Math.round(item.baseline_PX || 0),

      upliftVolume: Math.round(item.uplift_volume || 0),

      // productivities in your new JSON are productivity_CP / productivity_PX
      productivityCP: safeParseNumber(
        item.productivity_CP ?? item["productivity ratio_CP"] ?? item.productivity_ratio_CP
      ),
      productivityPX: safeParseNumber(
        item.productivity_PX ?? item["productivity ratio_PX"] ?? item.productivity_ratio_PX
      ),

      timeGranularity: item.Time_Granularity, // synthesized above
      year: item.year
    }));
  }

    const { brand, class: classData, manufacturer, value_sales, VSOD, VSOD_IYA, uplift_pct } = apiData;
    
    const categoryArray = brand || classData || manufacturer;
    const categoryKey = brand ? 'brand' : classData ? 'class' : 'manufacturer';

    if (!categoryArray || !Array.isArray(categoryArray)) {
      console.error(`${categoryKey} data is missing or not an array:`, categoryArray);
      return [];
    }

    return categoryArray.map((categoryName, index) => ({
        brand: categoryName, 
        promoValue: Math.round((value_sales && value_sales[index]) || 0),
        vsod: Math.round((VSOD && VSOD[index]) || 0),
        vsodIya: safeParseNumber(VSOD_IYA && VSOD_IYA[index]), 
        uplift: safeParseNumber(uplift_pct && uplift_pct[index]),
    }));
  };

  // useEffect(() => {
  //   function fetchHierarchyLevels() {
  //     if (!projectId) {
  //       console.warn("Project ID is not set in Redux state.");
  //       return;
  //     }
  //     fetch(`${getUrl()}/market_overview/hierarchy`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
  //       },
  //       body: JSON.stringify({ project_id: projectId }),
  //     })
  //       .then((response) => {
  //         if (!response.ok) {
  //           throw new Error(`HTTP error! Status: ${response.status}`);
  //         }
  //         return response.json();
  //       })
  //       .then((result) => {
  //         console.log("result from hierarcy", result);
  //         
  //         if (!result["0"] || Object.keys(result["0"]).length === 0) {
  //           throw new Error("No hierarchy data available");
  //         }
  //         
  //         const allowedHierarchyLevels = ["brand", "class", "manufacturer"];
  //         const rawHierarchyLevels = Object.keys(result["0"] || {}); 
  //         const hierarchyLevels = rawHierarchyLevels.filter((key) =>
  //           allowedHierarchyLevels.includes(key)
  //         );
  //         hierarchyLevels.push("channel", "retailer");
  //         setHierarchyOptions(hierarchyLevels);
  //       })
  //       .catch((error) => {
  //         console.error("Error fetching hierarchy levels:", error);
  //       });
  //   }
  //
  //   fetchHierarchyLevels();
  // }, [projectId]);


  // useEffect(() => {
  //   fetchPromotionData();
  // }, [projectId, selectedHierarchy, timePeriod, target]);

  // Load static data on mount
  useEffect(() => { fetchPromotionData(); }, [selectedPeriod]);

  useEffect(() => {
    setPage(0);
  }, [data]);

   useEffect(() => {
   setExpandedRows(new Set());
   setNestedData({});
 }, [selectedPeriod]);

  const handleRowToggle = (categoryName, rowIndex) => {
    const newExpandedRows = new Set(expandedRows); 
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
      
      if (!nestedData[categoryName]) {
        fetchNestedData(categoryName);
      }
    }
    setExpandedRows(newExpandedRows);
  };

  const groupNestedDataByBrand = (rawNestedData) => {
    const grouped = {};
    rawNestedData.forEach(item => {
      const brandName = item.brand;
      if (!grouped[brandName]) {
        grouped[brandName] = [];
      }
      grouped[brandName].push(item);
    });
    return grouped;
  };

  const fetchNestedData = (categoryName) => {

    const rawNested = [
  {
    "brand": "AFIA",
    "class": "COCONUT",
    "year": 2023,
    "value_sales": 175021.84,
    "volume_sales": 4444.0,
    "volume_sales_non_promo": 732.0,
    "VSOD": 3712.0,
    "volume_sales_CP": 2155.5,
    "volume_sales_PX": 0.0,
    "count_CP": 909.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 1423.5,
    "baseline_volume": 732.0,
    "baseline_PX": 732.0,
    "baseline_CP": 732.0,
    "uplift_pct": 4.0710382513661205,
    "productivity_PX": null,
    "productivity_CP": 1.566007
  },
  {
    "brand": "AFIA",
    "class": "COCONUT",
    "year": 2024,
    "value_sales": 775.5,
    "volume_sales": 5.5,
    "volume_sales_non_promo": 5.5,
    "VSOD": 0.0,
    "volume_sales_CP": 0.0,
    "volume_sales_PX": 0.0,
    "count_CP": 0.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -5.5,
    "baseline_volume": 5.5,
    "baseline_PX": 5.5,
    "baseline_CP": 5.5,
    "uplift_pct": -1.0,
    "productivity_PX": null,
    "productivity_CP": null
  },
  {
    "brand": "AFIA",
    "class": "CORN",
    "year": 2023,
    "value_sales": 52070339.38,
    "volume_sales": 4634964.5,
    "volume_sales_non_promo": 940223.8,
    "VSOD": 3694740.7,
    "volume_sales_CP": 1356076.2,
    "volume_sales_PX": 2235289.9,
    "count_CP": 9971.0,
    "count_PX": 4188.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 415852.4,
    "baseline_volume": 940223.8,
    "baseline_PX": 940223.8,
    "baseline_CP": 940223.8,
    "uplift_pct": 2.9296396240980074,
    "productivity_PX": 309.232593,
    "productivity_CP": 41.706188
  },
  {
    "brand": "AFIA",
    "class": "CORN",
    "year": 2024,
    "value_sales": 47857431.97,
    "volume_sales": 4959618.3,
    "volume_sales_non_promo": 748833.6,
    "VSOD": 4210784.7,
    "volume_sales_CP": 855687.1,
    "volume_sales_PX": 3315195.4,
    "count_CP": 14915.0,
    "count_PX": 8494.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 106853.5,
    "baseline_volume": 748833.6,
    "baseline_PX": 748833.6,
    "baseline_CP": 748833.6,
    "uplift_pct": 4.6231246835077915,
    "productivity_PX": 302.138192,
    "productivity_CP": 7.164164
  },
  {
    "brand": "AFIA",
    "class": "SUNFLOWER OIL",
    "year": 2023,
    "value_sales": 7164962.52,
    "volume_sales": 594203.5,
    "volume_sales_non_promo": 123727.1,
    "VSOD": 470476.4,
    "volume_sales_CP": 376076.4,
    "volume_sales_PX": 84835.8,
    "count_CP": 7791.0,
    "count_PX": 836.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 252349.3,
    "baseline_volume": 123727.1,
    "baseline_PX": 123727.1,
    "baseline_CP": 123727.1,
    "uplift_pct": 2.8025331556304156,
    "productivity_PX": -46.520694,
    "productivity_CP": 32.389847
  },
  {
    "brand": "AFIA",
    "class": "SUNFLOWER OIL",
    "year": 2024,
    "value_sales": 4331454.14,
    "volume_sales": 418585.3,
    "volume_sales_non_promo": 93189.1,
    "VSOD": 325396.2,
    "volume_sales_CP": 293078.5,
    "volume_sales_PX": 7084.7,
    "count_CP": 10150.0,
    "count_PX": 181.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 199889.4,
    "baseline_volume": 93189.1,
    "baseline_PX": 93189.1,
    "baseline_CP": 93189.1,
    "uplift_pct": 2.4917839103500303,
    "productivity_PX": -475.714917,
    "productivity_CP": 19.693537
  },
  {
    "brand": "AL ARABI",
    "class": "VEGETABLE OIL",
    "year": 2023,
    "value_sales": 32844586.92,
    "volume_sales": 3121072.8,
    "volume_sales_non_promo": 439938.6,
    "VSOD": 2681134.2,
    "volume_sales_CP": 655952.3,
    "volume_sales_PX": 1862600.4,
    "count_CP": 7568.0,
    "count_PX": 3263.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 216013.7,
    "baseline_volume": 439938.6,
    "baseline_PX": 439938.6,
    "baseline_CP": 439938.6,
    "uplift_pct": 5.0943372552442545,
    "productivity_PX": 435.9981,
    "productivity_CP": 28.543036
  },
  {
    "brand": "AL ARABI",
    "class": "VEGETABLE OIL",
    "year": 2024,
    "value_sales": 35936610.62,
    "volume_sales": 4749607.8,
    "volume_sales_non_promo": 1168378.8,
    "VSOD": 3581229.0,
    "volume_sales_CP": 674166.6,
    "volume_sales_PX": 2771501.5,
    "count_CP": 7743.0,
    "count_PX": 5233.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -494212.2,
    "baseline_volume": 1168378.8,
    "baseline_PX": 1168378.8,
    "baseline_CP": 1168378.8,
    "uplift_pct": 2.065126652417863,
    "productivity_PX": 306.348691,
    "productivity_CP": -63.826966
  },
  {
    "brand": "DALAL",
    "class": "VEGETABLE OIL",
    "year": 2023,
    "value_sales": 2086303.11,
    "volume_sales": 318928.5,
    "volume_sales_non_promo": 100485.0,
    "VSOD": 218443.5,
    "volume_sales_CP": 1092.0,
    "volume_sales_PX": 217351.5,
    "count_CP": 35.0,
    "count_PX": 547.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -99393.0,
    "baseline_volume": 100485.0,
    "baseline_PX": 100485.0,
    "baseline_CP": 100485.0,
    "uplift_pct": 1.1738916256157637,
    "productivity_PX": 213.649909,
    "productivity_CP": -2839.8
  },
  {
    "brand": "DALAL",
    "class": "VEGETABLE OIL",
    "year": 2024,
    "value_sales": 1960486.58,
    "volume_sales": 309190.5,
    "volume_sales_non_promo": 17731.5,
    "VSOD": 291459.0,
    "volume_sales_CP": 80602.5,
    "volume_sales_PX": 203026.5,
    "count_CP": 2657.0,
    "count_PX": 1428.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 62871.0,
    "baseline_volume": 17731.5,
    "baseline_PX": 17731.5,
    "baseline_CP": 17731.5,
    "uplift_pct": 15.4373572455799,
    "productivity_PX": 129.758403,
    "productivity_CP": 23.662401
  },
  {
    "brand": "HALAH",
    "class": "SUNFLOWER OIL",
    "year": 2023,
    "value_sales": 23126820.79,
    "volume_sales": 3184838.3,
    "volume_sales_non_promo": 623755.9,
    "VSOD": 2561082.4,
    "volume_sales_CP": 694012.0,
    "volume_sales_PX": 1829096.4,
    "count_CP": 5655.0,
    "count_PX": 2016.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 70256.1,
    "baseline_volume": 623755.9,
    "baseline_PX": 623755.9,
    "baseline_CP": 623755.9,
    "uplift_pct": 3.105904890037914,
    "productivity_PX": 597.887153,
    "productivity_CP": 12.423714
  },
  {
    "brand": "HALAH",
    "class": "SUNFLOWER OIL",
    "year": 2024,
    "value_sales": 17370465.72,
    "volume_sales": 2831320.6,
    "volume_sales_non_promo": 205977.5,
    "VSOD": 2625343.1,
    "volume_sales_CP": 638499.4,
    "volume_sales_PX": 1969010.2,
    "count_CP": 7584.0,
    "count_PX": 2759.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 432521.9,
    "baseline_volume": 205977.5,
    "baseline_PX": 205977.5,
    "baseline_CP": 205977.5,
    "uplift_pct": 11.745776116323384,
    "productivity_PX": 639.01149,
    "productivity_CP": 57.030841
  },
  {
    "brand": "LEEZA",
    "class": "CORN",
    "year": 2023,
    "value_sales": 2041486.44,
    "volume_sales": 193311.9,
    "volume_sales_non_promo": 25092.8,
    "VSOD": 168219.1,
    "volume_sales_CP": 168219.1,
    "volume_sales_PX": 0.0,
    "count_CP": 7203.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 143126.3,
    "baseline_volume": 25092.8,
    "baseline_PX": 25092.8,
    "baseline_CP": 25092.8,
    "uplift_pct": 5.703879200408085,
    "productivity_PX": null,
    "productivity_CP": 19.870373
  },
  {
    "brand": "LEEZA",
    "class": "CORN",
    "year": 2024,
    "value_sales": 1155894.16,
    "volume_sales": 108850.1,
    "volume_sales_non_promo": 17145.9,
    "VSOD": 91704.2,
    "volume_sales_CP": 81511.9,
    "volume_sales_PX": 0.0,
    "count_CP": 7620.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 64366.0,
    "baseline_volume": 17145.9,
    "baseline_PX": 17145.9,
    "baseline_CP": 17145.9,
    "uplift_pct": 4.3484623146058246,
    "productivity_PX": null,
    "productivity_CP": 8.446982
  },
  {
    "brand": "NAKHEEL",
    "class": "VEGETABLE OIL",
    "year": 2023,
    "value_sales": 15439224.02,
    "volume_sales": 2062003.5,
    "volume_sales_non_promo": 488095.3,
    "VSOD": 1573908.2,
    "volume_sales_CP": 465476.7,
    "volume_sales_PX": 1107797.0,
    "count_CP": 5260.0,
    "count_PX": 2226.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -22618.6,
    "baseline_volume": 488095.3,
    "baseline_PX": 488095.3,
    "baseline_CP": 488095.3,
    "uplift_pct": 2.2245920007834536,
    "productivity_PX": 278.392498,
    "productivity_CP": -4.300114
  },
  {
    "brand": "NAKHEEL",
    "class": "VEGETABLE OIL",
    "year": 2024,
    "value_sales": 10860319.79,
    "volume_sales": 1782663.2,
    "volume_sales_non_promo": 131256.3,
    "VSOD": 1651406.9,
    "volume_sales_CP": 465351.6,
    "volume_sales_PX": 1178079.8,
    "count_CP": 8115.0,
    "count_PX": 2735.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 334095.3,
    "baseline_volume": 131256.3,
    "baseline_PX": 131256.3,
    "baseline_CP": 131256.3,
    "uplift_pct": 11.58154389541683,
    "productivity_PX": 382.750823,
    "productivity_CP": 41.170092
  },
  {
    "brand": "O LITE",
    "class": "CANOLA OIL",
    "year": 2023,
    "value_sales": 3435735.01,
    "volume_sales": 247674.0,
    "volume_sales_non_promo": 86574.0,
    "VSOD": 161100.0,
    "volume_sales_CP": 150429.0,
    "volume_sales_PX": 0.0,
    "count_CP": 3818.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 63855.0,
    "baseline_volume": 86574.0,
    "baseline_PX": 86574.0,
    "baseline_CP": 86574.0,
    "uplift_pct": 0.8608358167579181,
    "productivity_PX": null,
    "productivity_CP": 16.724725
  },
  {
    "brand": "O LITE",
    "class": "CANOLA OIL",
    "year": 2024,
    "value_sales": 3161257.02,
    "volume_sales": 319563.0,
    "volume_sales_non_promo": 63276.0,
    "VSOD": 256287.0,
    "volume_sales_CP": 156312.0,
    "volume_sales_PX": 69885.0,
    "count_CP": 3855.0,
    "count_PX": 940.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 93036.0,
    "baseline_volume": 63276.0,
    "baseline_PX": 63276.0,
    "baseline_CP": 63276.0,
    "uplift_pct": 3.050303432581073,
    "productivity_PX": 7.030851,
    "productivity_CP": 24.133852
  },
  {
    "brand": "SHAMS",
    "class": "SUNFLOWER OIL",
    "year": 2023,
    "value_sales": 34112692.43,
    "volume_sales": 3287227.4,
    "volume_sales_non_promo": 365098.3,
    "VSOD": 2922129.1,
    "volume_sales_CP": 619935.9,
    "volume_sales_PX": 2166950.2,
    "count_CP": 7555.0,
    "count_PX": 4150.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": 254837.6,
    "baseline_volume": 365098.3,
    "baseline_PX": 365098.3,
    "baseline_CP": 365098.3,
    "uplift_pct": 7.0036776396932,
    "productivity_PX": 434.181181,
    "productivity_CP": 33.730986
  },
  {
    "brand": "SHAMS",
    "class": "SUNFLOWER OIL",
    "year": 2024,
    "value_sales": 33147795.25,
    "volume_sales": 4566406.1,
    "volume_sales_non_promo": 957595.6,
    "VSOD": 3608810.5,
    "volume_sales_CP": 682318.4,
    "volume_sales_PX": 2898050.9,
    "count_CP": 10509.0,
    "count_PX": 5214.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -275277.2,
    "baseline_volume": 957595.6,
    "baseline_PX": 957595.6,
    "baseline_CP": 957595.6,
    "uplift_pct": 2.7686164180370083,
    "productivity_PX": 372.162505,
    "productivity_CP": -26.194424
  },
  {
    "brand": "SLITE",
    "class": "SUNFLOWER OIL",
    "year": 2023,
    "value_sales": 150515.36,
    "volume_sales": 16824.0,
    "volume_sales_non_promo": 4621.5,
    "VSOD": 12202.5,
    "volume_sales_CP": 0.0,
    "volume_sales_PX": 0.0,
    "count_CP": 0.0,
    "count_PX": 0.0,
    "VSOD_IYA": 0.0,
    "uplift_volume": -4621.5,
    "baseline_volume": 4621.5,
    "baseline_PX": 4621.5,
    "baseline_CP": 4621.5,
    "uplift_pct": 1.6403765011359948,
    "productivity_PX": null,
    "productivity_CP": null
  }
];
    const withPeriod = rawNested.map(it => ({
    ...it,
    Time_Granularity: it.year === 2024 ? "P12M" : it.year === 2023 ? "P24M" : String(it.year),
  }));

  // Group by brand (same helper you already have)
  const grouped = groupNestedDataByBrand(withPeriod);

  // Pull this brand’s nested rows
  const brandNestedData = grouped[categoryName] || [];

  // Filter by period selected in the dropdown (P12M or P24M)
  const filteredNestedData = brandNestedData.filter(
    item => item.Time_Granularity === selectedPeriod
  );

  // Reuse your existing transformer (it already maps class, baseline, CP/PX, etc.)
  const transformedNestedData = transformApiData(filteredNestedData);
    
    setNestedData(prev => ({
      ...prev,
      [categoryName]: transformedNestedData
    }));
  };

  // const fetchNestedData = async (categoryFilter, customSelections = null) => {
  //   if (!projectId) {
  //     console.warn("Project ID is not set in Redux state.");
  //     return;
  //   }

  //   setLoadingNested(true);
  //   try {
  //     const apiUrl = `${getUrl()}/strategy_creation/promo_evolution_by_week`;
      
  //     const selections = customSelections || monthWeekSelections[categoryFilter] || defaultSelections;
  //     const calculatedWeek = calculateWeekNumber(selections.month, selections.week);
      
  //      const requestBody = {
  //        project_id: parseInt(projectId),
  //        hierarchy: selectedHierarchy,
  //        filters: { [selectedHierarchy]: categoryFilter },
  //        time_period: timePeriod,
  //        target: target,
  //        week: calculatedWeek,
  //      };
      
  //     const response = await fetch(apiUrl, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
  //       },
  //       body: JSON.stringify(requestBody),
  //     });

  //     console.log("Response status:", response.status);

  //     if (!response.ok) {
  //       const errorText = await response.text();
  //       console.error("API Error Response:", errorText);
  //       throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
  //     }

  //     const responseText = await response.text();
      
  //     const cleanedResponse = responseText.replace(/NaN/g, "null");
  //     const result = JSON.parse(cleanedResponse);
      
  //     const transformedData = transformNestedApiData(result);
  //   setNestedData(prev => ({
  //     ...prev,
  //     [categoryFilter]: transformedData
  //   }));
  //   } catch (error) {
  //     console.error("Error fetching nested data:", error);
  //   } finally {
  //   setLoadingNested(false);
  //   setLoadingSelections(prev => ({
  //     ...prev,
  //     [categoryFilter]: false
  //   }));
  //   }
  // };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Typography
        variant="h4"
        align="center"
        fontWeight={500}
        mb={1}
        sx={{
          fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" },
          px: { xs: 1, sm: 0 }
        }}
      >
        Promotion Evolution
      </Typography>

      {/* Year Filter Dropdown */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
          px: { xs: 2, sm: 0 }
        }}
      >
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: { xs: 120, sm: 150 },
            "& .MuiFormLabel-root": {
              fontSize: { xs: "0.8rem", sm: "0.9rem" }
            }
          }}
        >
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            sx={{
              backgroundColor: "white",
              fontWeight: 500,
              fontSize: { xs: "14px", sm: "16px" },
              minHeight: { xs: "36px", sm: "40px" },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
                borderRadius: "8px"
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            {availablePeriods.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
                {p === "P12M" ? "2024" : p === "P24M" ? "2023" : p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <FacebookCircularProgress loading={true} />
        </Box>
      ) : error ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={fetchPromotionData}
            message={`Error: ${error}`}
          />
        </Box>
      ) : data.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={fetchPromotionData}
            message="No promotion data available"
          />
        </Box>
      ) : (
        <>
          {/* Rows per page dropdown */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" },
              alignItems: "center",
              maxWidth: { xs: "95%", sm: "90%" },
              margin: "0 auto",
              mb: 2,
              px: { xs: 2, sm: 0 }
            }}
          >
            {/* Rows per page dropdown */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 0.5, sm: 1 },
              backgroundColor: { xs: "rgba(0,0,0,0.02)", sm: "transparent" },
              px: { xs: 2, sm: 0 },
              py: { xs: 1, sm: 0 },
              borderRadius: { xs: "8px", sm: 0 }
            }}>
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 500, 
                  fontSize: { xs: "0.75rem", sm: "0.85rem" }, 
                  color: "#555",
                  whiteSpace: "nowrap"
                }}
              >
                Rows:
              </Typography>
              <FormControl
                variant="standard"
                sx={{
                  minWidth: { xs: 50, sm: 60 },
                  "& .MuiInputBase-root": {
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  },
                  "& .MuiSelect-select": {
                    paddingRight: "24px",
                    paddingLeft: "8px",
                  },
                  "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                    borderBottom: "none",
                  },
                }}
              >
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  disableUnderline
                  IconComponent={ArrowDropDownIcon}
                >
                  {[5, 10, 25, 50].map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{ 
              maxWidth: { xs: "90%", sm: "96%", md: "95%" }, 
              margin: "0 auto",
              overflowX: "auto",
              overflowY: "hidden",
              borderRadius: { xs: "4px", sm: "8px" },
              boxShadow: { xs: "0 1px 4px rgba(0,0,0,0.06)", sm: "0 2px 8px rgba(0,0,0,0.1)" },
              "&::-webkit-scrollbar": {
                height: { xs: "4px", sm: "6px" },
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#9e9e9e",
                },
              },
            }}
          >
            <Table
              size={isSmallMobile ? "small" : "medium"}
              sx={{ 
                tableLayout: "fixed", 
                fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                minWidth: { xs: "550px", sm: "700px", md: "auto" },
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0",
                  padding: { xs: "2px 1px", sm: "4px 3px", md: "8px 5px" },
                  width: "10%"
                }
              }}
            >
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: theme.palette.grey[100],
                  "& .MuiTableCell-root": {
                    fontWeight: 600,
                    borderBottom: "2px solid #d0d0d0"
                  }
                }}>
                  <TableCell sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>Brand</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Vol Sales" : isMobile ? "Vol Sales" : "Volume Sales"}</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Non Promo" : isMobile ? "Non Promo" : "Volume Sales Non Promo"}</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>VSOD</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>Baseline</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>Uplift</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Vol CP" : isMobile ? "Vol CP" : "Volume Sales CP"}</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Vol PX" : isMobile ? "Vol PX" : "Volume Sales PX"}</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Prod CP" : isMobile ? "Prod CP" : "Productivity CP"}</b>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.75rem" },
                    fontWeight: 600
                  }}>
                    <b>{isSmallMobile ? "Prod PX" : isMobile ? "Prod PX" : "Productivity PX"}</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => (
                    <React.Fragment key={row.brand}>
                      <TableRow>
                        <TableCell sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          <Box display="flex" alignItems="center" gap={0.2}>
                            <IconButton
                              size="small"
                              onClick={() => handleRowToggle(row.brand, idx)}
                              sx={{ 
                                padding: { xs: 0.02, sm: 0.15 },
                                minWidth: { xs: "14px", sm: "auto" }
                              }}
                            >
                              {expandedRows.has(idx) ? (
                                <ExpandLess fontSize={isMobile ? "inherit" : "small"} />
                              ) : (
                                <ExpandMore fontSize={isMobile ? "inherit" : "small"} />
                              )}
                            </IconButton>
                            <Typography sx={{ 
                              fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {row.brand}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.volumeSales)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.volumeSalesNonPromo)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.vsod)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.baselineVolume)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.upliftVolume)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.volumeSalesCP)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          {formatNumber(row.volumeSalesPX)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          px: { xs: 0.2, sm: 0.4 },
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          <ColoredBox
                            number={Number.isFinite(row.productivityCP) ? row.productivityCP.toFixed(2) : "0.00"}
                            Color={
                              (row.productivityCP ?? 0) > 100
                                ? "#0BB783"
                                : (row.productivityCP ?? 0) === 100
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ 
                          px: { xs: 0.2, sm: 0.4 },
                          fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                        }}>
                          <ColoredBox
                            number={Number.isFinite(row.productivityPX) ? row.productivityPX.toFixed(2) : "0.00"}
                            Color={
                              (row.productivityPX ?? 0) > 100
                                ? "#0BB783"
                                : (row.productivityPX ?? 0) === 100
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        </TableCell>
                      </TableRow>

                      {/* Render nested rows */}
                      {expandedRows.has(idx) &&
                        (!nestedData[row.brand] ? (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              align="center"
                              sx={{ py: 2 }}
                            >
                              <FacebookCircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {/* Data Rows */}
                            {nestedData[row.brand] &&
                              nestedData[row.brand].length > 0 ? (
                              nestedData[row.brand].map(
                                (nestedRow, nestedIdx) => (
                                  <TableRow
                                    key={`nested-${row.brand}-${nestedIdx}`}
                                    sx={{ backgroundColor: "#f0f0f0" }}
                                  >
                                    <TableCell sx={{ 
                                      paddingLeft: { xs: 0.8, sm: 2 },
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      <Typography sx={{ 
                                        fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                      }}>
                                        {nestedRow.class || nestedRow.brand}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.volumeSales)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.volumeSalesNonPromo)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.vsod)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.baselineVolume)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.upliftVolume)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.volumeSalesCP)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      {formatNumber(nestedRow.volumeSalesPX)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      px: { xs: 0.2, sm: 0.4 },
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      <ColoredBox
                                        number={nestedRow.productivityCP?.toFixed(2) || "0.00"}
                                        Color={
                                          (nestedRow.productivityCP || 0) > 100
                                            ? "#0BB783"
                                            : (nestedRow.productivityCP || 0) === 100
                                            ? "#0095FF"
                                            : "#B22234"
                                        }
                                      />
                                    </TableCell>
                                    <TableCell align="center" sx={{ 
                                      px: { xs: 0.2, sm: 0.4 },
                                      fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }
                                    }}>
                                      <ColoredBox
                                        number={nestedRow.productivityPX?.toFixed(2) || "0.00"}
                                        Color={
                                          (nestedRow.productivityPX || 0) > 100
                                            ? "#0BB783"
                                            : (nestedRow.productivityPX || 0) === 100
                                            ? "#0095FF"
                                            : "#B22234"
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                )
                              )
                            ) : (
                              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableCell
                                  colSpan={10}
                                  align="center"
                                  sx={{ py: 2 }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#666",
                                      fontStyle: "italic",
                                      fontWeight: 500,
                                    }}
                                  >
                                    No data available
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 3,
              mb: 2,
              px: { xs: 2, sm: 0 },
              overflow: "hidden",
            }}
          >
            <Pagination
              count={Math.ceil(data.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, value) => setPage(value - 1)}
              color="primary"
              shape="rounded"
              size={isSmallMobile ? "small" : isMobile ? "small" : "medium"}
              showFirstButton={!isSmallMobile}
              showLastButton={!isSmallMobile}
              siblingCount={isSmallMobile ? 0 : isMobile ? 1 : 2}
              boundaryCount={isSmallMobile ? 1 : 2}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                  minWidth: { xs: "28px", sm: "32px", md: "36px" },
                  height: { xs: "28px", sm: "32px", md: "36px" },
                  margin: { xs: "0 1px", sm: "0 2px" }
                },
                "& .MuiPagination-ul": {
                  flexWrap: "wrap",
                  justifyContent: "center"
                },
                "& .MuiPaginationItem-page.Mui-selected": {
                  backgroundColor: "#1976d2",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
